import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import {
  getAllStarterTemplates,
  StarterTemplate,
  createStarterTemplate,
  updateStarterTemplate,
  deleteStarterTemplate,
  moveStarterTemplateUp,
  moveStarterTemplateDown,
  getFeaturedStarterTemplates,
  getStarterTemplatesByCategory,
  getStarterTemplatesByType,
} from "../firebase/starterTemplateService";
import { Timestamp } from "firebase/firestore";
import { withFirebaseRetry } from "../utils/retryUtils";
import { globalRateLimiter } from "../utils/rateLimiter";

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

interface StarterTemplateContextType {
  starterTemplates: StarterTemplate[];
  featuredStarterTemplates: StarterTemplate[];
  loading: boolean;
  error: Error | null;
  refreshStarterTemplates: () => Promise<void>;
  addStarterTemplate: (
    template: Omit<StarterTemplate, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editStarterTemplate: (
    id: string,
    template: Partial<Omit<StarterTemplate, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeStarterTemplate: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
  getTemplatesByCategory: (category: string) => Promise<StarterTemplate[]>;
  getTemplatesByType: (
    type: "template" | "extension" | "tool" | "boilerplate"
  ) => Promise<StarterTemplate[]>;
}

const StarterTemplateContext = createContext<
  StarterTemplateContextType | undefined
>(undefined);

export const StarterTemplateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [starterTemplates, setStarterTemplates] = useState<StarterTemplate[]>(
    []
  );
  const [featuredStarterTemplates, setFeaturedStarterTemplates] = useState<
    StarterTemplate[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  const fetchStarterTemplates = useCallback(
    async (forceRefresh: boolean = false) => {
      // Check if we have cached data and it's still fresh
      const now = Date.now();
      const isCacheValid =
        starterTemplates.length > 0 && now - lastFetchTime < CACHE_DURATION;

      if (!forceRefresh && isCacheValid) {
        console.log("Using cached starter templates data");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Use retry mechanism for fetching starter templates
        const [templatesData, featuredTemplatesData] = await withFirebaseRetry(
          async () => {
            return Promise.all([
              getAllStarterTemplates(),
              getFeaturedStarterTemplates(),
            ]);
          }
        );

        setStarterTemplates(templatesData);
        setFeaturedStarterTemplates(featuredTemplatesData);
        setLastFetchTime(now);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch starter templates data")
        );
        console.error("Error fetching starter templates:", err);
      } finally {
        setLoading(false);
      }
    },
    [starterTemplates.length, lastFetchTime]
  );

  // Fetch once on mount. NOTE: do NOT depend on `fetchStarterTemplates` here —
  // its useCallback deps include `lastFetchTime`, which the fetch itself updates,
  // so depending on it caused an infinite re-fetch loop whenever the collection
  // was empty (the `starterTemplates.length > 0` cache guard never short-circuits).
  useEffect(() => {
    fetchStarterTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshStarterTemplates = async () => {
    await fetchStarterTemplates(true); // Force refresh
  };

  const addStarterTemplate = async (
    template: Omit<StarterTemplate, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-add")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    const id = await withFirebaseRetry(async () =>
      createStarterTemplate(template)
    );

    // Update state directly instead of full refresh
    const newTemplate: StarterTemplate = {
      ...template,
      id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    setStarterTemplates((prev) => [...prev, newTemplate]);

    // Update featured templates if this template is featured
    if (template.featured) {
      setFeaturedStarterTemplates((prev) => [...prev, newTemplate]);
    }

    return id;
  };

  const editStarterTemplate = async (
    id: string,
    template: Partial<Omit<StarterTemplate, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-edit")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => updateStarterTemplate(id, template));

    // Update state directly instead of full refresh
    const updatedTemplate = { ...template, updatedAt: Timestamp.now() };

    setStarterTemplates((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, ...updatedTemplate } : item
      )
    );

    // Update featured templates if featured status changed
    if ("featured" in template) {
      if (template.featured) {
        const fullTemplate = starterTemplates.find((item) => item.id === id);
        if (fullTemplate) {
          setFeaturedStarterTemplates((prev) => {
            const exists = prev.some((item) => item.id === id);
            return exists
              ? prev.map((item) =>
                  item.id === id ? { ...item, ...updatedTemplate } : item
                )
              : [...prev, { ...fullTemplate, ...updatedTemplate }];
          });
        }
      } else {
        setFeaturedStarterTemplates((prev) =>
          prev.filter((item) => item.id !== id)
        );
      }
    } else {
      // Update existing featured template
      setFeaturedStarterTemplates((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, ...updatedTemplate } : item
        )
      );
    }
  };

  const removeStarterTemplate = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-remove")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => deleteStarterTemplate(id));

    // Update state directly instead of full refresh
    setStarterTemplates((prev) =>
      prev.filter((template) => template.id !== id)
    );
    setFeaturedStarterTemplates((prev) =>
      prev.filter((template) => template.id !== id)
    );
  };

  const moveUp = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-move")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => moveStarterTemplateUp(id));

    // For move operations, we need to refresh to get the correct order
    await fetchStarterTemplates(true);
  };

  const moveDown = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-move")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => moveStarterTemplateDown(id));

    // For move operations, we need to refresh to get the correct order
    await fetchStarterTemplates(true);
  };

  const getTemplatesByCategory = async (
    category: string
  ): Promise<StarterTemplate[]> => {
    return await getStarterTemplatesByCategory(category);
  };

  const getTemplatesByType = async (
    type: "template" | "extension" | "tool" | "boilerplate"
  ): Promise<StarterTemplate[]> => {
    return await getStarterTemplatesByType(type);
  };

  const value: StarterTemplateContextType = {
    starterTemplates,
    featuredStarterTemplates,
    loading,
    error,
    refreshStarterTemplates,
    addStarterTemplate,
    editStarterTemplate,
    removeStarterTemplate,
    moveUp,
    moveDown,
    getTemplatesByCategory,
    getTemplatesByType,
  };

  return (
    <StarterTemplateContext.Provider value={value}>
      {children}
    </StarterTemplateContext.Provider>
  );
};

export const useStarterTemplates = () => {
  const context = useContext(StarterTemplateContext);
  if (context === undefined) {
    throw new Error(
      "useStarterTemplates must be used within a StarterTemplateProvider"
    );
  }
  return context;
};
